import { projectsService } from '@/services/projectsService/projectsService';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { ProjectUsersResponse, ProjectUsersState } from '@/types/projectUser.types';

const initialState: ProjectUsersState = {
  data: [],
  counts: null,
  meta: null,
  loading: false,
  error: null,
  currentProjectId: null,
};

export const fetchProjectUsers = createAsyncThunk<
  ProjectUsersResponse,
  number,
  { rejectValue: string }
>('projectUsers/fetchByProjectId', async (projectId, { rejectWithValue }) => {
  try {
    const response = await projectsService.getProjectUsers(projectId);
    return response;
  } catch (error: any) {
    const message = error instanceof Error ? error.message : 'Error fetching project users';
    return rejectWithValue(message);
  }
});

export const deleteProjectUser = createAsyncThunk<
  { userId: number },
  { projectId: number; userId: number },
  { rejectValue: string }
>('projectUsers/deleteUser', async ({ projectId, userId }, { rejectWithValue }) => {
  try {
    await projectsService.deleteProjectConsentUser(projectId, userId);
    return { userId };
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || 'Failed to delete user');
  }
});

export const toggleProjectUserStatus = createAsyncThunk<
  { userId: number; status: boolean },
  { projectId: number; userId: number; status: boolean },
  { rejectValue: string }
>('projectUsers/toggleStatus', async ({ projectId, userId, status }, { rejectWithValue }) => {
  try {
    await projectsService.toggleConsentUserStatus(projectId, userId, status);

    return { userId, status };
  } catch (error: any) {
    const message = error instanceof Error ? error.message : 'Failed to toggle status';
    return rejectWithValue(message);
  }
});

const projectUsersSlice = createSlice({
  name: 'projectUsers',
  initialState,
  reducers: {
    clearProjectUsers(state) {
      state.data = [];
      state.counts = null;
      state.meta = null;
      state.currentProjectId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectUsers.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentProjectId = action.meta.arg;
      })
      .addCase(fetchProjectUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data || [];
        state.counts = action.payload.counts || null;
        state.meta = action.payload.meta || null;
      })
      .addCase(fetchProjectUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
      })

      .addCase(deleteProjectUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProjectUser.fulfilled, (state, action) => {
        state.loading = false;

        state.data = state.data.filter((u) => u.userId !== action.payload.userId);

        if (state.counts && state.counts.totalUsers > 0) {
          state.counts.totalUsers -= 1;
        }
      })

      .addCase(deleteProjectUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(toggleProjectUserStatus.fulfilled, (state, action) => {
        const { userId, status } = action.payload;
        const user = state.data.find((u) => u.userId === userId);
        if (user) {
          user.status = status;
        }

        if (state.counts) {
          if (status) {
            state.counts.totalActiveUsers += 1;
            state.counts.totalInactiveUsers -= 1;
          } else {
            state.counts.totalActiveUsers -= 1;
            state.counts.totalInactiveUsers += 1;
          }
        }
      });
  },
});

export const { clearProjectUsers } = projectUsersSlice.actions;
export default projectUsersSlice.reducer;
